import React, { memo, useCallback } from 'react'
import { IoChevronForward, IoWarningOutline, IoFlameOutline, IoTrophyOutline, IoArrowDownOutline } from 'react-icons/io5'
import type { FixtureCardSummary } from '../../../types'
import { ConfidenceIndicator } from './ConfidenceIndicator'
import { fmtKickoff } from '../../../utils'

interface FixtureCardItemProps {
  fixture: FixtureCardSummary
  onSelect: () => void
}

// Memoized component to prevent unnecessary re-renders in list
function FixtureCardItemComponent({ fixture, onSelect }: FixtureCardItemProps) {
  const hasProjection = fixture.projected_total_cards != null

  // Determine if this is a high-card match (above 4.5)
  const isHighCard = hasProjection && fixture.projected_total_cards! >= 4.5

  // Format kickoff time
  const kickoffTime = fmtKickoff(fixture.kickoff)

  return (
    <button
      type="button"
      className={`cards-fixture-item ${isHighCard ? 'high-card' : ''}`}
      onClick={onSelect}
      aria-label={`${fixture.home_team_name} vs ${fixture.away_team_name}, projected ${fixture.projected_total_cards?.toFixed(1) || 'N/A'} cards`}
    >
      <div className="cards-fixture-item-left">
        {/* Team Logos & Names */}
        <div className="cards-fixture-teams">
          <div className="cards-fixture-team">
            {fixture.home_team_logo && (
              <img
                src={fixture.home_team_logo}
                alt=""
                className="cards-team-logo"
                loading="lazy"
              />
            )}
            <span className="cards-team-name">{fixture.home_team_name}</span>
          </div>
          <span className="cards-fixture-vs">vs</span>
          <div className="cards-fixture-team">
            {fixture.away_team_logo && (
              <img
                src={fixture.away_team_logo}
                alt=""
                className="cards-team-logo"
                loading="lazy"
              />
            )}
            <span className="cards-team-name">{fixture.away_team_name}</span>
          </div>
        </div>

        {/* Match Info */}
        <div className="cards-fixture-info">
          <span className="cards-fixture-time">{kickoffTime}</span>
          {fixture.referee_name && (
            <span className="cards-fixture-referee">
              Ref: {fixture.referee_name}
              {fixture.high_card_referee && (
                <span className="cards-high-card-badge" title="High-card referee">
                  <IoWarningOutline size={12} />
                </span>
              )}
            </span>
          )}
          {!fixture.referee_assigned && (
            <span className="cards-fixture-referee cards-fixture-referee-tbd">Referee TBD</span>
          )}
        </div>

        {/* Context Badges */}
        <div className="cards-fixture-badges">
          {fixture.is_derby && (
            <span className="cards-badge cards-badge-derby" title="Derby match">
              <IoFlameOutline size={12} />
              Derby
            </span>
          )}
          {fixture.is_title_race && (
            <span className="cards-badge cards-badge-title" title="Title race">
              <IoTrophyOutline size={12} />
              Title Race
            </span>
          )}
          {fixture.is_relegation_battle && (
            <span className="cards-badge cards-badge-relegation" title="Relegation battle">
              <IoArrowDownOutline size={12} />
              Relegation
            </span>
          )}
        </div>
      </div>

      <div className="cards-fixture-item-right">
        {/* Projection */}
        <div className="cards-fixture-projection">
          {hasProjection ? (
            <>
              <span className="cards-projection-value">
                {fixture.projected_total_cards!.toFixed(1)}
              </span>
              <span className="cards-projection-label">cards</span>
            </>
          ) : (
            <span className="cards-projection-na">N/A</span>
          )}
        </div>

        {/* Confidence */}
        {fixture.confidence_score != null && fixture.confidence_level && (
          <ConfidenceIndicator
            score={fixture.confidence_score}
            level={fixture.confidence_level}
            compact
          />
        )}

        {/* Quick Insight */}
        {fixture.quick_insight && (
          <p className="cards-fixture-insight">{fixture.quick_insight}</p>
        )}

        <IoChevronForward size={16} className="cards-fixture-chevron" />
      </div>
    </button>
  )
}

// Export memoized version for performance
export const FixtureCardItem = memo(FixtureCardItemComponent, (prev, next) => {
  // Custom comparison - only re-render if fixture data or selection changed
  return (
    prev.fixture.fixture_id === next.fixture.fixture_id &&
    prev.fixture.projected_total_cards === next.fixture.projected_total_cards &&
    prev.fixture.confidence_score === next.fixture.confidence_score &&
    prev.fixture.is_saved === next.fixture.is_saved &&
    prev.onSelect === next.onSelect
  )
})
